// CV Builder paid pipeline — server functions used by the portal CV page.
// - getMyCvEntitlement: read free/paid balance for the signed-in user
// - createCvOrder: create a pending cv_orders row priced at R15 per CV,
//   then return the order reference so the client can launch PayFast.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

export const CV_UNIT_PRICE_ZAR = 15;
// Hard product rule: no CV checkout may exceed R30 (max 2 × R15).
export const CV_MAX_QUANTITY = 2;
export const CV_MAX_TOTAL_ZAR = 30;
/** Motivational letter — R50, or R50 for the CV + letter bundle. */
export const LETTER_PRICE_ZAR = 50;
export const BUNDLE_PRICE_ZAR = 50;
export type CvProduct = "cv" | "letter" | "bundle";


export const getMyCvEntitlement = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    await supabaseAdmin
      .from("cv_entitlements" as any)
      .upsert({ user_id: context.userId }, { onConflict: "user_id" });
    const [
      { data },
      { data: hasAccess },
      { data: sub },
      { data: prof },
      { data: isAdmin },
    ] = await Promise.all([
      supabaseAdmin
        .from("cv_entitlements" as any)
        .select("*")
        .eq("user_id", context.userId)
        .maybeSingle(),
      supabaseAdmin.rpc("has_active_access", { _user_id: context.userId }),
      supabaseAdmin
        .from("user_subscriptions")
        .select("status, plan_code, current_period_end")
        .eq("user_id", context.userId)
        .maybeSingle(),
      supabaseAdmin
        .from("profiles")
        .select("trial_ends_at, trial_started_at")
        .eq("id", context.userId)
        .maybeSingle(),
      supabaseAdmin.rpc("has_role", {
        _user_id: context.userId,
        _role: "admin",
      }),
    ]);
    const row: any = data ?? {
      free_used: false,
      paid_credits: 0,
      paid_credits_used: 0,
      total_generated: 0,
    };
    const paidRemaining = Math.max(
      0,
      Number(row.paid_credits || 0) - Number(row.paid_credits_used || 0),
    );
    const subActive = Boolean(
      sub &&
        (sub as any).status === "active" &&
        (sub as any).current_period_end &&
        new Date((sub as any).current_period_end).getTime() > Date.now(),
    );
    const trialActive = Boolean(
      prof &&
        (prof as any).trial_ends_at &&
        new Date((prof as any).trial_ends_at).getTime() > Date.now(),
    );
    const unlimited = Boolean(hasAccess);
    const tier: "admin" | "subscription" | "trial" | "paid" | "free" | "none" =
      isAdmin
        ? "admin"
        : subActive
          ? "subscription"
          : trialActive
            ? "trial"
            : paidRemaining > 0
              ? "paid"
              : !row.free_used
                ? "free"
                : "none";
    const letterRemaining = Math.max(
      0,
      Number(row.letter_credits || 0) - Number(row.letter_credits_used || 0),
    );
    return {
      tier,
      freeRemaining: row.free_used ? 0 : 1,
      paidCredits: Number(row.paid_credits || 0),
      paidCreditsUsed: Number(row.paid_credits_used || 0),
      paidRemaining,
      letterCredits: Number(row.letter_credits || 0),
      letterCreditsUsed: Number(row.letter_credits_used || 0),
      letterRemaining,
      canGenerateLetter: unlimited || letterRemaining > 0,
      totalGenerated: Number(row.total_generated || 0),
      canGenerate: unlimited || !row.free_used || paidRemaining > 0,
      unlimited,
      isAdmin: Boolean(isAdmin),
      trial: trialActive
        ? {
            startedAt: (prof as any)?.trial_started_at ?? null,
            endsAt: (prof as any).trial_ends_at,
          }
        : null,
      subscription: sub
        ? {
            status: (sub as any).status,
            plan_code: (sub as any).plan_code,
            current_period_end: (sub as any).current_period_end,
            active: subActive,
          }
        : null,
    };
  });

export const createCvOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        quantity: z.number().int().min(1).max(CV_MAX_QUANTITY).default(1),
        product: z.enum(["cv", "letter", "bundle"]).default("cv"),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const reference = `CV-${Date.now().toString(36).toUpperCase()}-${Math.random()
      .toString(36)
      .slice(2, 6)
      .toUpperCase()}`;

    const product = data.product;
    // Quantity means CV credits. Letters are single items; the bundle is
    // 2 CV credits + 1 motivational letter for R50.
    const quantity = product === "cv" ? data.quantity : product === "bundle" ? 2 : 0;
    const amount =
      product === "cv"
        ? +(CV_UNIT_PRICE_ZAR * data.quantity).toFixed(2)
        : product === "letter"
          ? LETTER_PRICE_ZAR
          : BUNDLE_PRICE_ZAR;

    // Defence-in-depth: a CV-only checkout may never exceed R30.
    if (product === "cv" && amount > CV_MAX_TOTAL_ZAR) {
      throw new Error(
        `CV total R${amount.toFixed(2)} exceeds the R${CV_MAX_TOTAL_ZAR} cap (max ${CV_MAX_QUANTITY} × R${CV_UNIT_PRICE_ZAR}).`,
      );
    }

    const { error } = await supabaseAdmin.from("cv_orders" as any).insert({
      user_id: context.userId,
      reference,
      product,
      quantity: Math.max(quantity, product === "letter" ? 0 : 1),
      unit_price_zar: product === "cv" ? CV_UNIT_PRICE_ZAR : amount,
      amount_zar: amount,
      status: "pending",
    });
    if (error) throw new Error(error.message);
    return { reference, quantity, product, amountZar: amount };
  });


export const listMyCvOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data } = await supabaseAdmin
      .from("cv_orders" as any)
      .select(
        "reference, quantity, unit_price_zar, amount_zar, status, paid_at, paid_provider_ref, receipt_sent_at, created_at, public_token",
      )
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(50);
    return { orders: (data ?? []) as any[] };
  });

export const listMyCvGenerationLog = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data } = await supabaseAdmin
      .from("cv_generation_log" as any)
      .select(
        "id, status, credit_source, credit_delta, balance_after, model, target_role, reason, reference, created_at",
      )
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(50);
    return { entries: (data ?? []) as any[] };
  });

/**
 * Consume one motivational-letter credit. Subscribers, trialists and admins
 * have unlimited letters and are never charged a credit.
 */
export const consumeLetterCredit = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: hasAccess } = await supabaseAdmin.rpc("has_active_access", {
      _user_id: context.userId,
    });
    if (hasAccess) return { source: "unlimited" as const };
    const { error } = await supabaseAdmin.rpc("consume_letter_credit" as any, {
      _user_id: context.userId,
    });
    if (error) {
      throw new Error(
        error.message.includes("NO_LETTER_CREDIT")
          ? `No motivational letter credits left. Buy a letter for R${LETTER_PRICE_ZAR} or subscribe for R25/month for unlimited letters.`
          : error.message,
      );
    }
    return { source: "credit" as const };
  });
